/**
 * PDF.js utilities — render first page as JPEG thumbnail
 * PDF.js loaded from CDN at runtime (no npm package needed)
 */

const PDFJS_URL  = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

let _loadPromise = null;

export function loadPDFJS() {
  if (window._pdfjsLib) return Promise.resolve(window._pdfjsLib);
  if (_loadPromise)      return _loadPromise;

  _loadPromise = new Promise((resolve, reject) => {
    const script  = document.createElement('script');
    script.src    = PDFJS_URL;
    script.onload = () => {
      try {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_URL;
        window._pdfjsLib = window.pdfjsLib;
        resolve(window._pdfjsLib);
      } catch(e) { reject(e); }
    };
    script.onerror = () => {
      _loadPromise = null;
      reject(new Error('Could not load PDF.js from CDN'));
    };
    document.head.appendChild(script);
  });

  return _loadPromise;
}

/**
 * Given a PDF File, renders page 1 to a JPEG blob.
 * Returns { thumbnailBlob: Blob, numPages: number }
 */
export async function generatePDFThumbnail(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const pdfjs = await loadPDFJS();
    const pdf   = await pdfjs.getDocument({ url: objectUrl }).promise;
    const numPages = pdf.numPages;

    const page     = await pdf.getPage(1);
    const baseVp   = page.getViewport({ scale: 1 });
    const scale    = Math.min(700 / baseVp.width, 3);
    const viewport = page.getViewport({ scale });

    const canvas   = document.createElement('canvas');
    canvas.width   = Math.round(viewport.width);
    canvas.height  = Math.round(viewport.height);
    const ctx      = canvas.getContext('2d');
    ctx.fillStyle  = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    const thumbnailBlob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.88));
    return { thumbnailBlob, numPages };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
